<script setup>
import { Dialog, DialogPanel, TransitionRoot } from "@headlessui/vue";
import { storeToRefs } from "pinia";
import { ref, computed, inject, onMounted } from "vue";
import { useAuthStore } from "@/stores";
import PhoneNumber from "../utilities/PhoneNumber.vue";
import { useTemplateRef } from "vue";

const isImpersonating = localStorage.getItem("impersonate_user") ? true : false;

const authUserStore = useAuthStore();
const { user } = storeToRefs(authUserStore);
const isOpen = computed(() => !user.value?.phone_number && !isImpersonating);
const newPhoneNumber = ref("");
const loading = ref(false);
const errorMessage = ref("");
const axios = inject("axios");
const phoneNumberRef = useTemplateRef("phoneNumberRef")

async function savePhoneNumber() {
  errorMessage.value = "";

  if (!newPhoneNumber.value.trim()) {
    errorMessage.value = "Phone number is required.";
    return;
  }

  if (!phoneNumberRef.value.formatted.valid) {
    errorMessage.value = "Please enter a valid phone number.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.post("/api/v3/user/save-phone-number", {
      phone_number: newPhoneNumber.value,
    });

    authUserStore.user.phone_number = response.data.phone_number;
  } catch (err) {
    errorMessage.value = "Failed to save phone number.";
    loading.value = false;
  } finally {
    loading.value = false;
    localStorage.setItem("showPhoneNumberDialog","false")
  }
}

onMounted(() => {
  localStorage.setItem("showPhoneNumberDialog","false")
})
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="() => {}">
      <div class="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all"
        >
          <Dialog.Title class="text-lg font-medium text-gray-900 mb-4">
            Provide Phone Number
          </Dialog.Title>

          <p class="text-sm text-gray-600 mb-4">
            Please provide your phone number to continue.
          </p>
          <PhoneNumber
            v-model="newPhoneNumber"
            default-code="US"
            placeholder="(123) 456-7890"
            friendly-value
            ref="phoneNumberRef"
          />
          <p v-if="errorMessage" class="text-red-500 text-sm mt-1">
            {{ errorMessage }}
          </p>

          <div class="mt-6 text-right">
            <button
              :disabled="loading"
              @click="savePhoneNumber"
              class="inline-flex h-8 items-center px-3 py-2 border border-transparent text-xs leading-4 font-medium rounded-md shadow-sm text-untitled-gray-600 bg-untitled-gray-200 hover:bg-untitled-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-untitled-gray-500"
            >
              {{ loading ? "Saving..." : "Save" }}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
